'use client';

import { Box } from '@mui/material';
import { SessionScope } from '@repo/api-gw-sdk';
import { redirect } from 'next/navigation';
import type { PropsWithChildren } from 'react';
import { Loader } from '@/components/shared/loader';
import { useNonProjectDal } from '@/contexts/useNonProjectDal';
import { ErrorPage } from '@/views/errorPage';

// UX-only gate (Design §9.1): server-side RequireOrgScope is the authoritative
// boundary. Held on the viewer fetch on purpose: an org session must not mount
// the account providers below, or they fire account-scope fetches (projects,
// roles, source accounts) that 403 under an org JWT before the redirect lands.
// The viewer fetch is already on the account load path (AuthChecker gates on
// it), so gating here serializes it ahead of its siblings rather than adding a
// new request.
export const AccountScopeGate = ({
  children
}: PropsWithChildren) => {
  const {
    nonProjectDal: dal
  } = useNonProjectDal();
  const {
    data: viewer,
    isLoading
  } = dal.users.useViewer();
  if (isLoading) {
    return <Box className='h-screen' sx={{
      background: 'var(--mui-palette-background-pageGradient)'
    }}>
        <Loader />
      </Box>;
  }
  if (viewer?.scope === SessionScope.SESSION_SCOPE_ORG) {
    redirect('/organization');
  }

  // Admit only a confirmed account session. A failed viewer fetch leaves `viewer`
  // undefined, which is not SESSION_SCOPE_ORG — falling through on that would mount
  // the account providers for whatever the session actually is.
  if (viewer?.scope !== SessionScope.SESSION_SCOPE_ACCOUNT) {
    return <ErrorPage title='Session unavailable' errorMessage='Could not load your session. Reload the page or sign in again.' showBackButton={false} />;
  }
  return children;
};